import { useEffect, useRef, useState } from 'react';

import { Button, Dropdown, Flex } from '@revenuemarket/cds';
import { typographyStyle } from '@revenuemarket/design';
import { unique } from '@revenuemarket/utils/arrayUtils';
import { Typography } from 'antd';
import { CategoryTreeItem } from '~apis/categories';
import { FlattenTreeResult, flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import { CashFlowType } from '~models/cashFlow';

import { filterSearchTreeList } from '../common/utils';
import CategoryTreeDropDown from './CategoryTreeDropDown';
import CategoryTreeDropDownItem from './CategoryTreeDropDownItem';
import useRecommendLabelOptions from './hooks/useRecommendLabelOptions';

/** 기타 영업비용 KEY */
export const OTHER_OPERATING_EXPENSES_CATEGORY_LABEL_KEY = '212_null';

interface Props {
  transactionId?: number;
  accountId?: number;
  triggerElement?: React.ReactNode;
  footerElement?: React.ReactNode;
  treeList?: FlattenTreeResult<CategoryTreeItem>[];
  style?: React.CSSProperties;
  defaultShow?: boolean;
  closeOnSelect?: boolean;
  onSelect?: (value: string, key: string[]) => void;
  onApply?: (item?: FlattenTreeResult<CategoryTreeItem>, selctedKey?: string[]) => void;
  onClear?: () => void;
  multiple?: boolean;
  defaultActiveKey?: string[];
  applyLoading?: boolean;
  cashFlowType?: CashFlowType;
  treeRef?: React.RefObject<HTMLDivElement>;
  wrapperRef?: React.RefObject<HTMLDivElement>;
  additionalRef?: React.RefObject<HTMLDivElement>;
  /** 비활성화 아이템도 action이 가능히게 하려면 disableActionOnInactiveItem = false (ex: 분류 관리 page) */
  disableActionOnInactiveItem?: boolean;
  dropDownStyle?: React.CSSProperties;
  showReset?: boolean;
  headerElement?: React.ReactNode;
  disableScroll?: boolean;
  autoClose?: boolean;
  getPopupContainer?: () => HTMLElement;
}

const CategoryTreeSelect: React.FC<Props> = ({
  headerElement,
  triggerElement,
  treeList = [],
  footerElement,
  defaultShow,
  defaultActiveKey,
  closeOnSelect = false,
  onApply,
  onClear,
  onSelect,
  applyLoading,
  accountId,
  transactionId,
  cashFlowType,
  treeRef,
  wrapperRef,
  additionalRef,
  // 비활성화 아이템도 action이 가능히게 하려면 disableActionOnInactiveItem = false (ex: 분류 관리 page)
  disableActionOnInactiveItem = true,
  multiple = false,
  getPopupContainer,
  dropDownStyle,
  showReset = false,
  disableScroll,
  autoClose,
}) => {
  const [activeKey, setActiveKey] = useState<string[] | null>(defaultActiveKey ?? null);

  const recommendLabelOptions = useRecommendLabelOptions({
    activeKey,
    accountId,
    transactionId,
    treeList,
  });

  const [show, setShow] = useState(defaultShow ?? false);
  const [searchValue, setSearchValue] = useState('');
  const inputRef = useRef<any>(null); // Input ref 생성

  const handleActiveKey = ({
    targetKey,
    node,
    selected,
  }: {
    targetKey: string;
    node: FlattenTreeResult<CategoryTreeItem>;
    selected?: boolean;
  }) => {
    const childrenTree = flattenTree(node?.children ?? []);
    if (node.value?.isActive === false && disableActionOnInactiveItem) return;
    if (!selected) {
      const targetTree = [...childrenTree.map((item) => item.key), targetKey];

      const activeKeys = unique([...(activeKey ?? []), ...targetTree]).filter(Boolean);

      const target = node?.value?.categoryLabelKey;

      if (multiple) {
        onSelect?.(target ?? '', activeKeys);
        setActiveKey(activeKeys);
      } else {
        onSelect?.(target ?? '', [targetKey]);
        setActiveKey([targetKey]);
      }
    } else {
      const targetTree = [...childrenTree.map((item) => item.key), targetKey];
      const activeKeys = (activeKey ?? [])
        .filter((key) => !targetTree.includes(key))
        .filter(Boolean);
      const selectedValue = node.value?.categoryLabelKey;

      if (multiple) {
        onSelect?.(selectedValue ?? '', activeKeys);
        setActiveKey(activeKeys);
      } else {
        onSelect?.(selectedValue ?? '', []);
        setActiveKey([]);
      }
    }
  };

  const handleTreeItemClick = (key: string) => {
    const selected = treeList.find((node) => node.key === key);
    if (!selected?.value?.isActive && disableActionOnInactiveItem) return;
    if (multiple && selected) {
      handleActiveKey({
        targetKey: key,
        node: selected,
        selected: activeKey?.includes(key),
      });
      return;
    }

    if (!selected?.value?.isActive && disableActionOnInactiveItem) {
      return;
    }

    if (closeOnSelect) {
      setShow(false);
    }

    if (multiple) {
      const selectedKey = unique([...(activeKey ?? []), key]);
      setActiveKey(selectedKey);
      const selectedValue = selected?.value?.categoryLabelKey;
      if (selectedValue) {
        onSelect?.(selectedValue, selectedKey);
      }
    } else {
      const selectedKey = [key];
      setActiveKey(selectedKey);
      const selectedValue = selected?.value?.categoryLabelKey;
      if (selectedValue) {
        onSelect?.(selectedValue, selectedKey);
      }
    }
  };

  useEffect(() => {
    if (defaultActiveKey && treeRef?.current) {
      setActiveKey(defaultActiveKey);
      // 해당 cell로 scroll
      const selectedNode = treeRef?.current?.querySelector(`[data-key="${defaultActiveKey[0]}"]`);

      if (selectedNode) {
        setTimeout(() => {
          selectedNode.scrollIntoView({
            block: 'center',
            inline: 'center',
          });
        }, 100);
      }
    }
  }, [defaultActiveKey, treeRef]);

  useEffect(() => {
    setShow(defaultShow ?? false);
  }, [defaultShow]);

  useEffect(() => {
    if (show && inputRef.current) {
      // 드롭다운이 열릴 때 Input에 포커스 설정
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
    if (show === false) {
      // 드롭다운이 닫힐 때 Input에 포커스 해제
      setSearchValue('');
    }
  }, [show]);

  const searchResults = filterSearchTreeList<CategoryTreeItem>(treeList, searchValue);
  const categoryOptionList = searchValue ? searchResults : treeList;

  const handleSelectAllFiltered = () => {
    if (!multiple) return;

    const filteredKeys = categoryOptionList
      .filter((node) => node.value?.isActive && !node.isDisabled)
      .map((node) => node.key);

    const childrenTree = categoryOptionList
      .filter((node) => node.value?.isActive && !node.isDisabled)
      .flatMap((node) => flattenTree(node?.children ?? []).map((item) => item.key));

    const allKeys = unique([...(activeKey || []), ...filteredKeys, ...childrenTree]);

    setActiveKey(allKeys);

    // Trigger onSelect for each filtered option
    categoryOptionList
      .filter((node) => node.value?.isActive && !node.isDisabled)
      .forEach((node) => {
        onSelect?.(node.value?.categoryLabelKey || '', allKeys);
      });
  };

  const handleClearAllFiltered = () => {
    if (!multiple) return;

    const filteredKeys = categoryOptionList
      .filter((node) => node.value?.isActive && !node.isDisabled)
      .map((node) => node.key);

    const childrenTree = categoryOptionList
      .filter((node) => node.value?.isActive && !node.isDisabled)
      .flatMap((node) => flattenTree(node?.children ?? []).map((item) => item.key));

    const keysToRemove = [...filteredKeys, ...childrenTree];
    const newActiveKeys = (activeKey || []).filter((key) => !keysToRemove.includes(key));

    setActiveKey(newActiveKeys);

    // Trigger onSelect for clearing
    if (categoryOptionList.length > 0) {
      const firstNode = categoryOptionList.find((node) => node.value?.isActive && !node.isDisabled);
      onSelect?.(firstNode?.value?.categoryLabelKey || '', newActiveKeys);
    }
  };

  return (
    <Dropdown
      disableScroll={disableScroll}
      defaultOpen={defaultShow}
      onDropdownOpenChange={(open) => {
        if (!open) {
          setActiveKey(null);
          setSearchValue('');
          onClear?.();
        } else {
          setActiveKey((prev) =>
            prev === null || prev.length === 0 ? (defaultActiveKey ?? []) : prev,
          );
        }
      }}
      autoClose={autoClose}
      placement="bottomLeft"
      getPopupContainer={getPopupContainer}
      dropdownRender={(menu) => (
        <div
          ref={wrapperRef}
          style={{
            width: '100%',
          }}
        >
          <CategoryTreeDropDown
            wrapperRef={wrapperRef}
            headerElement={headerElement}
            menu={menu}
            treeList={treeList}
            activeKey={activeKey}
            multiple={multiple}
            setActiveKey={setActiveKey}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            inputRef={inputRef}
            footerElement={footerElement}
            treeRef={treeRef}
            setShow={setShow}
            show={show}
            dropDownStyle={dropDownStyle}
            onSelect={onSelect}
            onApply={onApply}
            onClear={onClear}
            applyLoading={applyLoading}
            disableActionOnInactiveItem={disableActionOnInactiveItem}
            cashFlowType={cashFlowType}
            onSelectAll={handleSelectAllFiltered}
            onClearAllSelect={handleClearAllFiltered}
          />
        </div>
      )}
      menu={{
        //@ts-ignore
        items: [
          ...recommendLabelOptions,
          ...categoryOptionList.map((node, index) => {
            const disabled = node.depth === 0 || node?.isDisabled || !node.value?.isActive;

            return {
              key: node.key + (disabled ? '-group' : ''),
              type: disabled ? 'group' : 'item',
              id: node.key,
              label: (
                <CategoryTreeDropDownItem
                  key={node.key}
                  multiple={multiple}
                  targetIndex={index}
                  node={node}
                  onSelect={onSelect}
                  activeKey={activeKey ?? []}
                  closeOnSelect={closeOnSelect}
                  handleActiveKey={handleActiveKey}
                  setActiveKey={setActiveKey}
                  disabled={disabled}
                />
              ),
            };
          }),
        ],

        onKeyDownCapture: (e) => {
          if (e.key === 'Enter') {
            if (!closeOnSelect) {
              e.stopPropagation();
              const targetKey = (e.target as HTMLElement).getAttribute('id');
              if (targetKey) handleTreeItemClick(targetKey);
            }
          }
        },
        onClick: (item) => {
          handleTreeItemClick(item.key);
        },
      }}
    >
      <Flex style={{ width: 'fit-content' }}>
        {triggerElement || (
          <Button disable={treeList.length === 0}>
            <Typography
              style={{
                ...typographyStyle['14_400'],
              }}
            >
              {' '}
              바꾸기
            </Typography>
          </Button>
        )}
      </Flex>
    </Dropdown>
  );
};

export default CategoryTreeSelect;
